import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { RegionSetStatusType } from "@app/projects/enumerators";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { TerminatePanelMode, WrappedTerminateProvidersPanel as TerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { Toggle } from "@app/shared/Toggle";
import { getNewPatientClaimsDefinitions, getClaimsVolumeDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ExportService from "@app/exports/exportService";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ClaimsActivityVolumeLabelEnum, PciDashboardProviderTerminationStatusEnum, PciDashboardProviderTerminationTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { PciDashboardTerminationDetails } from "@app/projects/network-assessment/pci-dashboard/claims-activity/PciDashboardTerminationDetails";
import { PciDashboardDetailsModel } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardDetailsModel";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import PciDashboardFiltersOverview from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersOverview";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import { PciDashboardProviderFlyout } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderFlyout";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { ColumnConfig, TextAlignment, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

interface PciDashboardDetailsViewProps {
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  showLineOfBusiness: boolean;
}

interface PciDashboardDetailsViewWaffleToppings {
  commercialClaimsLabel: string;
  effectivenessRank: string;
  efficiencyRank: string;
  key: string;
  medicaidClaimsLabel: string;
  medicareClaimsLabel: string;  
  providerName: JSX.Element;
  totalClaimsLabel: string;
  tricareClaimsLabel: string;
  vaClaimsLabel: string;
  terminationReason?: JSX.Element;
  commercialNewPatientInd: string;
  medicaidNewPatientInd: string;
  medicareNewPatientInd: string;
  totalNewPatientInd: string;
  vaNewPatientInd: string;
  tricareNewPatientInd: string;
}

const DashboardDetailsView = (props: PciDashboardDetailsViewProps): JSX.Element => {
  const [createReportLoading, setCreateReportLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardDetailsViewToppings, setDashboardDetailsViewToppings] = useState<PciDashboardDetailsViewWaffleToppings[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);
  const [totalCount, setTotalCount] = useState<number>(null);
  const [totalNotTerminatedCount, setTotalNotTerminatedCount] = useState<number>(null);
  const [showTerminateButton, setShowTerminateButton] = useState<boolean>(false);
  const [showTerminatedProviders, setShowTerminatedProviders] = useState<boolean>(false);
  const [npis, setNpis] = useState<string[]>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [terminatedNpisExist, setTerminatedNpisExist] = useState<boolean>(false);
  const [npiAndTerminationId, setNpiAndTerminationId] = useState<[string, number][]>([]);
  const [isNetworkGhostFilters, setIsNetworkGhostFilters] = useState<boolean>(false);
  const [error, setError] = useState("");
  
  const exportService = new ExportService();
  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const pciDashboardService = new PciDashboardService();
  const providerTerminationService = new ProviderTerminationService();
  const projectContext = useContext(CountyProjectContext);
  const user = React.useContext(UserContext);

  function getEffectivenessDefinition(): JSX.Element {
    return (
      <div className="text-start">
        Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to
        other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care.
      </div>
    );
  }

  function getEfficencyDefinition(): JSX.Element {
    return (
      <div className="text-start">
        Efficiency is a measure of physician cost vs peers where 5 is the best score (lower cost) and 1 is the worst
        score (higher cost). Efficiency measures are based on 1) cost per visit 2) cost per patient. Standardized pricing
        methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences.
      </div>
    );
  }

  useEffect(() => {
    if (!!projectContext.project && !!projectContext.runConfiguration) {
      getDashboardDetails(showTerminatedProviders, null, null, null, true);
    }
  }, [projectContext.project, projectContext.runConfiguration, projectContext]);

  const columnConfig: ColumnConfig = {
    commercialClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Commercial Claims Volume",
      testId: "commercial-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...{
      commercialNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Commercial New Patient Claims",
        testId: "commercial-new-patient-claims",
        tooltip: getNewPatientClaimsDefinitions()
      },
    },
    effectivenessRank: {
      alignment: TextAlignment.Left,
      displayText: "Effectiveness",
      testId: "effectiveness",
      tooltip: getEffectivenessDefinition()
    },
    efficiencyRank: {
      alignment: TextAlignment.Left,
      displayText: "Efficiency",
      testId: "efficiency",
      tooltip: getEfficencyDefinition()
    },
    medicaidClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicaid Claims Volume",
      testId: "medicaid-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...{
      medicaidNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Medicaid New Patient Claims",
        testId: "medicaid-new-patient-claims",
        tooltip: getNewPatientClaimsDefinitions()
      },
    },
    medicareClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicare Claims Volume",
      testId: "medicare-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...{
      medicareNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Medicare New Patient Claims",
        testId: "medicare-new-patient-claims",
        tooltip: getNewPatientClaimsDefinitions()
      },
    },
    providerName: {
      alignment: TextAlignment.Left,
      displayText: "Name",
      testId: "name",
    },
    totalClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Total Claims Volume",
      testId: "total-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...{
      totalNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Total New Patient Claims",
        testId: "total-new-patient-claims",
        tooltip: getNewPatientClaimsDefinitions()
      },
    },
    ...(projectContext.project.showVaClaimsVolumeColumn && {
      vaClaimsLabel: {
        alignment: TextAlignment.Left,
        displayText: "VA Claims Volume",
        testId: "va-claims-volume",
        tooltip: getClaimsVolumeDefinitions()
      },
      ...{
        vaNewPatientInd: {
          alignment: TextAlignment.Left,
          displayText: "VA New Patient Claims",
          testId: "va-new-patient-claims",
          tooltip: getNewPatientClaimsDefinitions()
        }
      },
    }),
    ...(projectContext.project.showTricareClaimsVolumeColumn && {
      tricareClaimsLabel: {
        alignment: TextAlignment.Left,
        displayText: "TRICARE Claims Volume",
        testId: "tricare-claims-volume",
        tooltip: getClaimsVolumeDefinitions()
      },
      ...{
        tricareNewPatientInd: {
          alignment: TextAlignment.Left,
          displayText: "TRICARE New Patient Claims",
          testId: "tricare-new-patient-claims",
          tooltip: getNewPatientClaimsDefinitions()
        }
      },
    }),
    ...(showTerminatedProviders && {
      terminationReason: {
        alignment: TextAlignment.Left,
        displayText: "Reason",
        minWidth: "85px",
        noSort: true,
        testId: "reason-label"
      }
    })
  };

  const customSort = (
    records: PciDashboardDetailsViewWaffleToppings[],
    column: keyof PciDashboardDetailsViewWaffleToppings,
    sortedDirection: SortDirections
  ): PciDashboardDetailsViewWaffleToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);

    getDashboardDetails(showTerminatedProviders, true, column, direction);

    return records;
  }

  const getDashboardDetails = (
    showTerminated?: boolean,
    isSorting?: boolean,
    column: string = null,
    direction: SortDirection = null,
    isReloading?: boolean,
    loadingMore?: boolean,
  ) => {
    setLoading(true);

    const filtersPayload: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filtersPayload.sortedColumn = isSorting ? column : sortedColumn;
    filtersPayload.sortDirection = isSorting ? direction : sortDirection;
    filtersPayload.take = isSorting ? dashboardDetailsViewToppings.length : 100;
    filtersPayload.skip = (isSorting || isReloading || !dashboardDetailsViewToppings.length) ? 0 : dashboardDetailsViewToppings.length;

    if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkTypeEnum.Network && !filtersPayload.compareScenario) {
      filtersPayload.ignoreTake = true;
    }

    // if all specialties, counties, or county statuses are selected send an empty array to save resources
    if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
      filtersPayload.countySSAs = [];
    }

    if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
      filtersPayload.specialtyGroupCodes = [];
    }

    if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
      filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
      filtersPayload.countyStatuses = [];
    }

    const buildData = (data: PciDashboardDetailsModel, isNetworkGhostFilters?: boolean, loadingMore?: boolean) => {
      if (!!data) {
        let waffleToppings: PciDashboardDetailsViewWaffleToppings[];
        if (isNetworkGhostFilters && (user.roles.includes(Roles.TERM_PROVIDER_VIEWER) || user.roles.includes(Roles.TERM_PROVIDER_ADMIN))) {
          setNpiAndTerminationId([])
          waffleToppings = data.summaries.map((summary, index) => {
            /* oxlint-disable sort-keys */
            if (loadingMore) {
              const existingNpi = dashboardDetailsViewToppings.some((details: { key: string }) => {
                const npi = details.key.split('-')[0];
                if (npi == summary.npi) {
                  return npi
                }
              })
              if (existingNpi) {
                return null;
              }
            }
            if (!showTerminated ?
              (summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.NotTerminated)
              :
              (summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination)) {

              if (!summary.terminationId && !!summary.providerTermination?.providerTerminationProjectIds) {
                setNpiAndTerminationId((prevNpiandId: [string, number][]) => [...prevNpiandId, ...summary.providerTermination.providerTerminationProjectIds.map((termId: number) => [summary.npi, termId] as [string, number])]);
              }
              else {
                setNpiAndTerminationId((prevNpiandId: [string, number][]) => [...prevNpiandId, [summary.npi, summary.terminationId]]);
              }
              return {
                key: `${summary.npi}-${index}`,
                providerName:
                  <>
                    <FlyoutLink
                      classNames="p-0 qes-button-secondary"
                      content={{
                        body: getProviderProfile(summary.providerId, summary.providerName, filtersPayload.providerNetworkType, summary.countySSA, summary.specialtyGroupCode, summary.specialtyGroupName, summary.npi) as JSX.Element,
                        title: "Provider Profile",
                      }}
                      testId={`pci-dashboard__provider-details-flyout--${summary.npi}`}
                    >
                      {summary.providerName ? summary.providerName : summary.npi}
                    </FlyoutLink>
                    <span className="text-muted ml15 d-block">NPI: {summary.npi}</span>
                  </>,
                specialtyGroupName: summary.specialtyGroupName,
                effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
                efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
                commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
                commercialNewPatientInd: !!summary.commercialNewPatientInd ? summary.commercialNewPatientInd : "---",
                medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
                medicaidNewPatientInd: !!summary.medicaidNewPatientInd ? summary.medicaidNewPatientInd : "---",
                medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
                medicareNewPatientInd: !!summary.medicareNewPatientInd ? summary.medicareNewPatientInd : "---",
                vaClaimsLabel: !!summary.vaClaimsLabel ? summary.vaClaimsLabel : "---",
                vaNewPatientInd: !!summary.vaNewPatientInd ? summary.vaNewPatientInd : "---",
                tricareClaimsLabel: !!summary.tricareClaimsLabel ? summary.tricareClaimsLabel : "---",
                tricareNewPatientInd: !!summary.tricareNewPatientInd ? summary.tricareNewPatientInd : "---",
                totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
                totalNewPatientInd: !!summary.totalNewPatientInd ? summary.totalNewPatientInd : "---",
                terminationReason: (() => {

                  let terminationString = "";
                  if (summary.providerTermination?.terminateByNPI) {
                    terminationString += "NPI";
                    if (summary.providerTermination?.terminateByTIN) {
                      terminationString += " and TIN";
                    }
                  } else if (summary.providerTermination?.terminateByTIN) {
                    terminationString += "TIN";
                  }

                  return (
                    <>
                      <div className="mb-3">
                        {summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination && <div className="demibold">UNPROCESSED</div>}
                        {user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && (
                          <TooltipWrapper text={!!summary.providerTermination?.providerTerminationProjectId ? "Edit/View Terminated Provider Details" : ""}>
                            <FlyoutLink
                              disabled={!summary.providerTermination?.providerTerminationProjectId}
                              classNames="text-info fw-normal p-0"
                              content={{
                                body: updateOrViewTerminatedProviderPanel(summary.providerTermination, true),
                                title: "Termination Details",
                              }}
                              testId={`pci-dashboard__termination-details-flyout--edit--${summary.npi}`}
                            >
                              {summary.providerTermination?.reasonName ? summary.providerTermination?.reasonName : `Includes Multiple Terminations` }
                            </FlyoutLink>
                          </TooltipWrapper>
                        )}
                        {((!user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && user.roles.includes(Roles.TERM_PROVIDER_VIEWER))) && (
                          <TooltipWrapper text={!!summary.providerTermination?.providerTerminationProjectId ? "View Terminated Provider Details" : ""}>
                            <FlyoutLink
                              classNames="text-info fw-normal p-0"
                              content={{
                                body: updateOrViewTerminatedProviderPanel(summary.providerTermination, false),
                                title: "Termination Details",
                              }}
                              testId={`pci-dashboard__termination-details-flyout--no-edit--${summary.npi}`}
                            >
                              {summary.providerTermination?.reasonName ? summary.providerTermination?.reasonName : `Includes Multiple Terminations` }
                            </FlyoutLink>
                          </TooltipWrapper>
                        )}
                      </div>
                      <div>
                        {terminationString != "" ?
                          `Terminated by ${terminationString}`
                          :
                          ""
                        }
                      </div>
                    </>
                  );
                })(),
              };
            }
            else {
              return null
            }
            /* oxlint-enable sort-keys */
          }).filter((topping) => topping !== null);
        }
        else {
          waffleToppings = data.summaries.map((summary, index) => {
            /* oxlint-disable sort-keys */
            return {
              key: `${summary.npi}-${index}`,
              providerName:
                <>
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                        body: getProviderProfile(summary.providerId, summary.providerName, filtersPayload.providerNetworkType, summary.countySSA, summary.specialtyGroupCode, summary.specialtyGroupName, summary.npi) as JSX.Element,
                        title: "Provider Profile",
                    }}
                    testId={`pci-dashboard__provider-details-flyout--${summary.npi}`}
                  >
                    {summary.providerName ? summary.providerName : summary.npi}
                  </FlyoutLink>
                  <span className="text-muted ml15 d-block">NPI: {summary.npi}</span>
                </>,
              specialtyGroupName: summary.specialtyGroupName,
              effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
              efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
              commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
              commercialNewPatientInd: !!summary.commercialNewPatientInd ? summary.commercialNewPatientInd : "---",
              medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
              medicaidNewPatientInd: !!summary.medicaidNewPatientInd ? summary.medicaidNewPatientInd : "---",
              medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
              medicareNewPatientInd: !!summary.medicareNewPatientInd ? summary.medicareNewPatientInd : "---",
              vaClaimsLabel: !!summary.vaClaimsLabel ? summary.vaClaimsLabel : "---",
              vaNewPatientInd: !!summary.vaNewPatientInd ? summary.vaNewPatientInd : "---",
              tricareClaimsLabel: !!summary.tricareClaimsLabel ? summary.tricareClaimsLabel : "---",
              tricareNewPatientInd: !!summary.tricareNewPatientInd ? summary.tricareNewPatientInd : "---",
              totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
              totalNewPatientInd: !!summary.totalNewPatientInd ? summary.totalNewPatientInd : "---",
            };
            /* oxlint-enable sort-keys */
          });
        }

        if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkTypeEnum.Network && !filtersPayload.compareScenario) {
          setTotalNotTerminatedCount(data.totalNotTerminatedCount);

          const loadMoreCount = dashboardDetailsViewToppings.length == 0 ? 100 : dashboardDetailsViewToppings.length
          waffleToppings = waffleToppings.slice(0, isReloading ? 100 : loadMoreCount)
        }

        if (isSorting || !dashboardDetailsViewToppings.length || isReloading) {
          setDashboardDetailsViewToppings(waffleToppings);
          setSelectedKeys([]);
        } else {
          let newToppings: PciDashboardDetailsViewWaffleToppings[] = cloneDeep(dashboardDetailsViewToppings);
          newToppings = newToppings.concat(waffleToppings);
          setDashboardDetailsViewToppings(newToppings);
        }

        setTotalCount(data.totalCount);
      }
      
      setLoading(false);
    }

    if (props.showLineOfBusiness) {
      if (!loadingMore) {
        filtersPayload.skip = 0;
      }
      pciDashboardService.getClaimsActivityDashboardDetails(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((data) => {

        if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkTypeEnum.Network && !filtersPayload.compareScenario && (user.roles.includes(Roles.TERM_PROVIDER_ADMIN) || user.roles.includes(Roles.TERM_PROVIDER_VIEWER))) {
          pciDashboardService.getNetworkGhostProviderTerminationRecords(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((termRecords) => {

            if (!showTerminatedProviders) {
              setShowTerminateButton(true);
            }

            const notTermedNpis = termRecords
              .filter((record) => record.terminationStatus == PciDashboardProviderTerminationStatusEnum.NotTerminated)
              .map((notTermed) => notTermed.npi);

            setNpis(notTermedNpis);

            const updatedSummaries = data.summaries.map(summary => {
              const matchingRecord = termRecords.find(record => record.npi == summary.npi);

              if (matchingRecord) {
                if (matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination) {
                  setTerminatedNpisExist(true);
                }
                if (matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination) {
                  matchingRecord.terminateByNPI = matchingRecord.identifier == PciDashboardProviderTerminationTypeEnum.NPI ? true : false;
                  matchingRecord.terminateByTIN = matchingRecord.identifier == PciDashboardProviderTerminationTypeEnum.TIN ? true : false;
                  return {
                    ...summary,
                    providerTermination: matchingRecord,
                    terminationId: matchingRecord.providerTerminationProjectId,
                    terminationStatus: matchingRecord.terminationStatus
                  }
                }
                return { ...summary, terminationStatus: matchingRecord.terminationStatus }
              }
              return summary;
            });

            data.summaries = updatedSummaries;
            data.totalNotTerminatedCount = notTermedNpis.length;

            if (data.totalNotTerminatedCount == data.totalCount) {
              setShowTerminatedProviders(false);
              setTerminatedNpisExist(false);
              if (showTerminated) {
                getDashboardDetails(false, null, null, null, true);
                return;
              }
            }
            setIsNetworkGhostFilters(true);
            buildData(data, true, loadingMore);
          }, (error) => {
            setError(error);
          });
        }
        else {
          setTotalNotTerminatedCount(totalCount);
          setShowTerminateButton(false);
          buildData(data);
        }
      }, (error) => {
        setError(error);
      });
    } else {
      pciDashboardService.getClaimsInsightsDashboardDetails(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((data) => {
        buildData(data);
      }, (error) => {
        setError(error);
      });
    }
  }

  const handleToggle = () => {
    setShowTerminatedProviders((prevState: boolean) => {
      const newState = !prevState;
      getDashboardDetails(newState, null, null, null, true);
      return newState;
    });
  }

  const handleToast = (errorStatus?: string) => {
    const toastText = !!errorStatus ?
      `${errorStatus} - There was an error submitting the report. Please try again, or contact your Client Services Representative.` :
      "Report was successfully submitted!"

    toast(toastText, {
      autoClose: !!errorStatus && false,
      containerId: "z-index-9999",
      type: !!errorStatus ? "error" : "success",
    });

    setCreateReportLoading(false);
  }

  const onCreateReportClick = () => {
    setCreateReportLoading(true);
    const filtersPayload: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

    exportService.postExportPciDashboardDetails(projectContext.project.projectId, projectContext.runConfiguration.projectRunConfigurationId, filtersPayload).then((response) => {
      if (!!response) {
        handleToast();
      }
    }).catch((error) => {
      handleToast(error?.status);
    });
  }

  const createTerminateProvidersPanel = () => {
    return (
      <TerminateProvidersPanel
        onSubmit={() => {
          projectContext.setProjectHasUnprocessedTerminations(true);
          setDashboardDetailsViewToppings([]);
        }}
        projectId={projectContext.project.projectId}
        npis={selectedKeys.length == 0 ? npis : selectedKeys.map((key: string) => key.split('-')[0])}
        terminatePanelMode={TerminatePanelMode.Create}
        showAffiliationsInsights={projectContext.project.showAffiliationsInsights && projectContext.project.affiliationsDisplayOnNetworkAccordion}
      />
    );
  }

  const updateOrViewTerminatedProviderPanel = (terminatedProvider: PciDashboardTerminationDetails, canEdit: boolean) => {
    
    return (
      <TerminateProvidersPanel
        projectId={projectContext.project.projectId}
        terminatePanelMode={canEdit ? TerminatePanelMode.Edit : TerminatePanelMode.View}
        termination={terminatedProvider}
        onSubmit={() => {
          getDashboardDetails(true, null, null, null, true);
        }}
        showAffiliationsInsights={projectContext.project.showAffiliationsInsights && projectContext.project.affiliationsDisplayOnNetworkAccordion}
      />
    )
  }

  const onBackToFullViewClick = () => {
    const filters: PciDashboardFiltersPayload = pciDashboardFiltersContext.getFiltersPayload();
    pciDashboardFiltersContext.setDisplayedFilters(filters);
    props.setShowDashboardDetails(false);
  }

  const onCheckboxChange = (newSelectedKeys: string[]) => {
    setSelectedKeys(newSelectedKeys);
  }

  function getProviderProfile(providerId: number, providerName: string, providerNetworkType: ProviderNetworkTypeEnum, countySSA: string, specialtyGroupCode: string, specialtyGroupName : string, npi: string): JSX.Element {
    return (
      <PciDashboardProviderFlyout
        providerId={providerId}
        providerName={providerName}
        providerNetworkType={providerNetworkType}
        project={projectContext.project}
        runConfiguration={projectContext.runConfiguration}
        countySSA={countySSA}
        specialtyGroupCode={specialtyGroupCode}
        specialtyGroupName={specialtyGroupName}
        npi={npi} />
    );
  }

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Dashboard Details">
        <PageHeaderRight>
          <QESButton
            onClick={() => onBackToFullViewClick()}
            qesTestId="pci-dashboard__dashboard-details--back-to-full-view"
            qesType="primary"
          >
            Back to Full View
          </QESButton>
        </PageHeaderRight>
      </PageHeader>
      <PciDashboardFiltersOverview
        isDashboardDetailsView={true}
        showLineOfBusiness={props.showLineOfBusiness}
        showNewPatientClaims={props.showLineOfBusiness && !!pciDashboardFiltersContext.newPatientClaimsFilters.length}
      />
      <div className="row">
        {terminatedNpisExist && (
          <div className="col-12 d-flex justify-content-end">
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
              <Toggle
                label="Terminated"
                active={showTerminatedProviders}
                onChange={handleToggle}
                testId="pci-dashboard__dashboard-details--toggle-terminated-providers"
              />
            </RoleRestricted>
          </div>
        )}
        <div className="col-12 pci-dashboard__details-hide-all-checkbox">
          <Waffles<PciDashboardDetailsViewWaffleToppings>
            bulkActionBar={(
              <div>
                <QESButton
                  className="px-0"
                  disabled={createReportLoading}
                  onClick={() => onCreateReportClick()}
                  qesTestId="pci-dashboard__dashboard-details--create-report"
                  qesType="secondary"
                >
                  Create Report
                </QESButton>

                {showTerminateButton && !showTerminatedProviders && (
                  <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                    {!dashboardDetailsViewToppings.length ? (
                        <FlyoutLink
                          classNames="p-3"
                          content={{
                            body: createTerminateProvidersPanel(),
                            title: `Terminate Ghost Providers`,
                          }}
                          disabled={true}
                          testId="pci-dasboard__dashboard-details--terminate-flyout-disabled"
                        >
                          <QesIcon iconName={QesIcons.UserXV1} isDecorative={true} className="align-text-top" /> Terminate
                        </FlyoutLink>
                    ): (
                        <TooltipWrapper text={selectedKeys.length ? (selectedKeys.length == 1 ? `Terminate selected provider` : `Terminate selected providers`) : `Terminate all network ghost providers`} containerClassName="ms-3">
                          <FlyoutLink
                            classNames="p-0"
                            content={{
                              body: createTerminateProvidersPanel(),
                              title: `Terminate Ghost Providers`,
                            }}
                            disabled={false}
                            testId="pci-dasboard__dashboard-details--terminate-flyout"
                          >
                            <QesIcon iconName={QesIcons.UserXV1} isDecorative={true} className="align-text-top" /> {selectedKeys.length ? "Terminate" : "Terminate All Providers"}
                          </FlyoutLink>
                        </TooltipWrapper>
                    )}
                  </RoleRestricted>
                )}

                {showTerminatedProviders && (
                  <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                    <TooltipWrapper text={`${!selectedKeys.length ? "Restore all network ghost providers" : (selectedKeys.length > 1 || !selectedKeys.length) ? "Restore selected providers" : "Restore selected provider"}`} containerClassName="ms-3">
                      <ModalLink
                        content={{
                          body:
                            <div>
                              <QESAlert classNames="mb-3" type="warning" persist={true}>
                                {`This will restore all terminations associated with the selected ${!selectedKeys.length || selectedKeys.length > 1 ? "NPIs" : "NPI"}.`}
                              </QESAlert>
                              <div>
                                {`${!selectedKeys.length || selectedKeys.length > 1 ? "Are you sure you want to restore the selected providers?" : "Are you sure you want to restore the selected provider?"}`}
                              </div>
                            </div>,
                          cancelButtonText: "Cancel",
                          confirmButtonAction: async () => {
                            const selectedNpis = selectedKeys.map((key: string) => key.split('-')[0]);

                            const terminateIds = !selectedNpis.length ?
                              npiAndTerminationId
                                .map((npiAndTermId: [string, number]) => npiAndTermId[1])
                              :
                              npiAndTerminationId
                                .filter(([npi]: [string, number]) => selectedNpis.includes(npi))
                                .map(([, id]: [string, number]) => id);

                            await providerTerminationService.deleteTermination(
                              projectContext.project.projectId,
                              terminateIds
                            );

                            const allRestored = !selectedKeys.length || selectedKeys.length === dashboardDetailsViewToppings.length;

                            if (allRestored) {
                              setShowTerminatedProviders(false);
                            }

                            const unprocessedTerminations = await providerTerminationService.getUnprocessedProviderTerminations(projectContext.project.projectId);
                            projectContext.setProjectHasUnprocessedTerminations((unprocessedTerminations?.length ?? 0) > 0);

                            getDashboardDetails(allRestored ? false : showTerminatedProviders, null, null, null, true);
                          },
                          confirmButtonText: "Restore",
                          title: `${selectedKeys.length > 1 ? "Restore Providers" : "Restore Provider"}`,
                        }}
                        testId="pci-dashboard__restore-modal"
                        classNames="p-0"
                      >
                        <QesIcon iconName={QesIcons.UserV1} isDecorative={true} className="align-text-top" /> {!selectedKeys.length ? "Restore All Providers" : "Restore"}
                      </ModalLink>
                    </TooltipWrapper>
                  </RoleRestricted>
                )}
              </div>
            )}
            columns={columnConfig}
            customSort={customSort}
            data={dashboardDetailsViewToppings}
            handleCheckboxChange={onCheckboxChange}
            fullWidth={true}
            loading={loading}
            passThroughSorting={true}
            selectedKeys={
              dashboardDetailsViewToppings
                .filter((provider: PciDashboardDetailsViewWaffleToppings) =>
                  selectedKeys.includes(provider.key)
                )
                .map((provider: PciDashboardDetailsViewWaffleToppings) => provider.key)
            }
            showCheckboxes={showTerminateButton && user.roles.includes(Roles.TERM_PROVIDER_ADMIN)}
            testId="pci-dashboard__dashboard-details-table"
            totalNumberOfRecords={dashboardDetailsViewToppings.length}
          />

          {(
            (isNetworkGhostFilters
              ? (showTerminatedProviders
                ? (totalCount - (totalNotTerminatedCount ?? 0)) > dashboardDetailsViewToppings.length
                : (totalNotTerminatedCount ?? totalCount) > dashboardDetailsViewToppings.length)
              : totalCount > dashboardDetailsViewToppings.length
            ) && (
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <QESButton
                      className="mt-3"
                      disabled={loading}
                      onClick={() => getDashboardDetails(showTerminatedProviders, null, null, null, null, true)}
                      qesTestId="pci-dashboard__dashboard-details--load-more"
                      qesType="primary"
                    >
                      Load More
                    </QESButton>
                  </div>
                </div>
              </div>
            )
          )}

        </div>
      </div>
    </React.Fragment>
  )
}

export default DashboardDetailsView;
